import { useState } from "react";
import type { ReactNode } from "react";
import { PortalContainerContext } from "@tellescope/design-system";
import { useThemeStyles } from "../hooks/useThemeStyles";
import { useFontLoader } from "../hooks/useFontLoader";
import { usePortalTheming } from "../store/portalSchemaStore";

interface OrganizationThemeProviderProps {
  children: ReactNode;
}

/**
 * Provider component that applies organization theme colors to its children.
 *
 * Scoping approach: CSS custom properties are applied as inline styles on a
 * `display: contents` wrapper div. This means the vars cascade to all descendant
 * elements without the wrapper participating in layout. Crucially, this keeps
 * theme vars scoped to the enduser portal — they do NOT bleed into the admin UI.
 *
 * Radix portals (Dialog, Sheet) render into document.body by default, escaping
 * the wrapper div and losing access to the scoped CSS vars. To fix this, we
 * provide a portal outlet div (inside the styled wrapper) via PortalContainerContext.
 * Dialog (@tellescope/design-system) and Sheet (@tellescope/ui) consume this context to redirect
 * their portal content into the outlet div, which inherits the scoped vars.
 *
 * Mounted once in `App.tsx`, around the whole enduser `/*` route element — so the unauthenticated
 * sign-in journey is branded too, not just the portal behind the login. Never nest a second one:
 * that double-mounts `useFontLoader` and re-scopes the Radix portal outlet.
 */
export const OrganizationThemeProvider: React.FC<OrganizationThemeProviderProps> = ({ children }) => {
  const theming = usePortalTheming();
  const themeStyles = useThemeStyles(theming);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // Load custom fonts for the portal (global font, heading font, body font)
  useFontLoader(theming?.fontFamily, theming?.customFontUrl, theming?.headingFont, theming?.bodyFont);

  return (
    <PortalContainerContext.Provider value={portalContainer}>
      {/* Scoped wrapper: CSS vars applied here cascade to all children.
          display: contents removes this div from layout so it doesn't affect children.
          Radix portaled content (Dialog, Sheet) mounts inside via portalContainer ref,
          ensuring it also inherits the scoped CSS vars. */}
      <div style={themeStyles} className="contents">
        {children}
        {/* Portal outlet: Radix Dialog/Sheet content mounts here (inside the styled
            wrapper) instead of document.body, so it inherits the scoped theme vars. */}
        <div ref={setPortalContainer} />
      </div>
    </PortalContainerContext.Provider>
  );
};
